import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Sidebar from '../Dashboard/Sidebar';
import Loader from '../layouts/Loader';
import MetaData from '../layouts/MetaData';
import {
    getAdminTreatments,
    createTreatment,
    updateTreatment,
    deleteTreatment,
} from '../../actions/treatmentActions';
import {
    clearTreatmentError,
    clearTreatmentCreated,
    clearTreatmentUpdated,
    clearTreatmentDeleted,
} from '../../slices/treatmentSlice';

import DeleteConfirmModal from '../layouts/DeleteConfirmModal';

const currency = (n) =>
    `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function TreatmentList() {
    const dispatch = useDispatch();
    const { loading, treatments = [], error, isCreated, isUpdated, isDeleted } =
        useSelector((s) => s.treatmentState);
    const { user } = useSelector((state) => state.authState);
    const isAdmin = user?.role === 'admin' || user?.role === 'master';

    const [newName, setNewName] = useState('');
    const [newPrice, setNewPrice] = useState('');

    /* ---- Inline edit state ---- */
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editPrice, setEditPrice] = useState('');
    const [editActive, setEditActive] = useState(true);

    useEffect(() => {
        dispatch(clearTreatmentCreated());
        dispatch(clearTreatmentUpdated());
        dispatch(clearTreatmentDeleted());
        dispatch(clearTreatmentError());
        dispatch(getAdminTreatments());
        return () => {
            dispatch(clearTreatmentCreated());
            dispatch(clearTreatmentUpdated());
            dispatch(clearTreatmentDeleted());
            dispatch(clearTreatmentError());
        };
    }, [dispatch]);

    useEffect(() => {
        if (error) {
            toast(error, { type: 'error' });
            dispatch(clearTreatmentError());
        }
        if (isCreated) {
            toast('Treatment added', { type: 'success' });
            dispatch(clearTreatmentCreated());
            setNewName('');
            setNewPrice('');
        }
        if (isUpdated) {
            toast('Treatment updated', { type: 'success' });
            dispatch(clearTreatmentUpdated());
            setEditId(null);
        }
        if (isDeleted) {
            toast('Treatment deleted', { type: 'success' });
            dispatch(clearTreatmentDeleted());
        }
    }, [error, isCreated, isUpdated, isDeleted, dispatch]);

    const handleAdd = (e) => {
        e.preventDefault();
        if (!newName.trim()) { toast('Enter treatment name', { type: 'error' }); return; }
        if (newPrice === '' || isNaN(newPrice) || Number(newPrice) < 0) {
            toast('Enter a valid price', { type: 'error' }); return;
        }
        dispatch(createTreatment({ name: newName.trim(), price: Number(newPrice) }));
    };

    const startEdit = (t) => {
        setEditId(t._id);
        setEditName(t.name);
        setEditPrice(t.price);
        setEditActive(t.active);
    };

    const handleUpdate = (id) => {
        if (!editName.trim()) { toast('Name cannot be empty', { type: 'error' }); return; }
        dispatch(updateTreatment(id, { name: editName.trim(), price: Number(editPrice), active: editActive }));
    };

    const [deleteModal, setDeleteModal] = useState({ show: false, id: null, name: '' });

    const handleDelete = (id, name) => {
        setDeleteModal({ show: true, id, name });
    };

    const confirmDelete = () => {
        dispatch(deleteTreatment(deleteModal.id));
        setDeleteModal({ show: false, id: null, name: '' });
    };

    const btnStyle = (bg, color, border) => ({
        background: bg, color, border,
        borderRadius: 7, fontWeight: 500, fontSize: '0.8rem', padding: '0.3rem 0.8rem',
        cursor: 'pointer', transition: 'opacity 0.15s',
    });

    return (
        <div className="row g-0">
      <div className="col-12 col-md-2"><Sidebar /></div>
      <div className="col-12 col-md-10 main-content">
                <MetaData title="Treatments" />

                {/* Page header */}
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                    <div>
                        <h1 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>
                            Treatments
                            <span className="ms-2" style={{
                                fontSize: '0.78rem', fontWeight: 600,
                                color: 'var(--primary)',
                                background: 'rgba(13,148,136,0.1)',
                                border: '1px solid rgba(13,148,136,0.25)',
                                padding: '0.2em 0.7em', borderRadius: 8,
                            }}>
                                {treatments.length}
                            </span>
                        </h1>
                        <p className="mb-0 mt-1" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            Manage treatment types and their billing prices
                        </p>
                    </div>
                </div>

                {/* ---- Add new treatment card ---- */}
                {isAdmin && (
                    <div className="card mb-4">
                        <div className="card-body p-4">
                            <h6 className="fw-bold mb-3" style={{
                                fontSize: '0.75rem', textTransform: 'uppercase',
                                letterSpacing: '0.05em', color: 'var(--text-muted)'
                            }}>
                                <i className="fa fa-plus me-1" style={{ color: 'var(--primary)' }}></i>
                                Add New Treatment
                            </h6>
                            <form onSubmit={handleAdd}>
                                <div className="row g-3 align-items-end">
                                    <div className="col-12 col-sm-5">
                                        <label className="form-label">Treatment Name</label>
                                        <input
                                            className="form-control"
                                            placeholder="e.g. Varma Therapy"
                                            value={newName}
                                            onChange={(e) => setNewName(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-12 col-sm-4">
                                        <label className="form-label">Price (₹)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="form-control"
                                            placeholder="e.g. 1500"
                                            value={newPrice}
                                            onChange={(e) => setNewPrice(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-12 col-sm-3">
                                        <button
                                            type="submit"
                                            className="btn btn-primary w-100 py-2 fw-bold"
                                            disabled={loading}
                                        >
                                            <i className="fa fa-save me-2"></i> Save
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* ---- Treatments table ---- */}
                {loading ? (
                    <Loader />
                ) : treatments.length === 0 ? (
                    <div className="card text-center py-5" style={{ color: 'var(--text-muted)' }}>
                        <i className="fa fa-stethoscope fa-3x mb-3" style={{ opacity: 0.25 }}></i>
                        <p className="mb-0">No treatments added yet. Add your first treatment above.</p>
                    </div>
                ) : (
                    <div className="card">
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0" style={{ fontSize: '0.875rem' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '40%' }}>Treatment Name</th>
                                            <th style={{ width: '20%' }}>Price</th>
                                            <th style={{ width: '15%' }}>Status</th>
                                            {isAdmin && <th className="text-end" style={{ width: '25%' }}>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {treatments.map((t) =>
                                            editId === t._id ? (
                                                /* ---- Inline edit row ---- */
                                                <tr key={t._id} style={{ background: 'rgba(13,148,136,0.04)' }}>
                                                    <td>
                                                        <input
                                                            className="form-control form-control-sm"
                                                            value={editName}
                                                            onChange={(e) => setEditName(e.target.value)}
                                                            autoFocus
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            className="form-control form-control-sm"
                                                            value={editPrice}
                                                            onChange={(e) => setEditPrice(e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="form-check form-switch mb-0">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                checked={editActive}
                                                                onChange={(e) => setEditActive(e.target.checked)}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                            <label className="form-check-label" style={{ fontSize: '0.78rem', color: editActive ? 'var(--success)' : 'var(--text-muted)' }}>
                                                                {editActive ? 'Active' : 'Inactive'}
                                                            </label>
                                                        </div>
                                                    </td>
                                                    {isAdmin && (
                                                        <td className="text-end">
                                                            <button
                                                                className="btn btn-sm me-1"
                                                                style={btnStyle('rgba(16,185,129,0.1)', '#065f46', '1px solid rgba(16,185,129,0.3)')}
                                                                onClick={() => handleUpdate(t._id)}
                                                                disabled={loading}
                                                            >
                                                                <i className="fa fa-check me-1"></i>Save
                                                            </button>
                                                            <button
                                                                className="btn btn-sm"
                                                                style={btnStyle('rgba(100,116,139,0.08)', '#475569', '1px solid rgba(100,116,139,0.2)')}
                                                                onClick={() => setEditId(null)}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ) : (
                                                /* ---- Display row ---- */
                                                <tr key={t._id}>
                                                    <td className="fw-semibold">{t.name}</td>
                                                    <td style={{ color: 'var(--primary)', fontWeight: 600 }}>
                                                        {currency(t.price)}
                                                    </td>
                                                    <td>
                                                        <span style={{
                                                            background: t.active ? 'rgba(16,185,129,0.1)' : 'rgba(100,116,139,0.1)',
                                                            color: t.active ? '#065f46' : '#475569',
                                                            border: t.active ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(100,116,139,0.2)',
                                                            padding: '0.25em 0.65em', borderRadius: 6,
                                                            fontSize: '0.73rem', fontWeight: 600,
                                                        }}>
                                                            {t.active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    {isAdmin && (
                                                        <td className="text-end">
                                                            <button
                                                                className="btn btn-sm me-1"
                                                                style={btnStyle('rgba(13,148,136,0.08)', 'var(--primary)', '1px solid rgba(13,148,136,0.2)')}
                                                                onClick={() => startEdit(t)}
                                                            >
                                                                <i className="fa fa-pencil me-1"></i>Edit
                                                            </button>
                                                            <button
                                                                className="btn btn-sm"
                                                                style={btnStyle('rgba(239,68,68,0.08)', 'var(--danger)', '1px solid rgba(239,68,68,0.2)')}
                                                                onClick={() => handleDelete(t._id, t.name)}
                                                            >
                                                                <i className="fa fa-trash me-1"></i>Delete
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                <p className="mt-3" style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                    <i className="fa fa-info-circle me-1"></i>
                    Active treatments appear in the billing form dropdown. Inactive treatments are hidden from billing but not deleted.
                </p>
                
                <DeleteConfirmModal
                    show={deleteModal.show}
                    itemName={deleteModal.name}
                    onClose={() => setDeleteModal({ show: false, id: null, name: '' })}
                    onConfirm={confirmDelete}
                />
        </div>
        </div>
    );
}




